import { useRef, useState, useMemo, useEffect, type ChangeEvent } from 'react';
import { Box, Button, HStack, VStack, Text, Input, InputGroup, InputRightElement, InputLeftElement, Select, Tabs, TabList, Tab, Badge, IconButton, useDisclosure, Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, Flex, Menu, MenuButton, MenuList, MenuItem, Card, CardHeader, CardBody, Heading } from '@chakra-ui/react';
import { Search, KeySquare, Plus, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { DataTable, type DataTableRef, type DataTableOptions } from '@/components/tables';
import { useToastNotification } from '@/lib/utils/useToastNotification';
import { useAccessCheck } from '@/hooks/useAccess';
import { apiClient } from '@/lib/api/client';
import { useThemeColors } from '@/hooks/useThemeColors';
import UserModal from '@/components/modals/AddUserModal';
import { UserType } from '@/types';

type TabValue = 'all' | 'trash';

export default function UsersPage() {
    const colors = useThemeColors();
    const navigate = useNavigate();
    const tableRef = useRef<DataTableRef>(null);
    const searchRef = useRef<HTMLInputElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { showError, showSuccess, showWarning } = useToastNotification();
    const { isOpen, onOpen, onClose } = useDisclosure();

    const [activeTab, setActiveTab] = useState<TabValue>('all');
    const [searchQuery, setSearchQuery] = useState('');
    const [totalRows, setTotalRows] = useState(0);
    const [totalTrash, setTotalTrash] = useState(0);
    const [bulkAction, setBulkAction] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [roles, setRoles] = useState<any[]>([]);
    const [isImporting, setIsImporting] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [isBulkProcessing, setIsBulkProcessing] = useState(false);
    const [modalAction, setModalAction] = useState<'bulk' | 'role'>('bulk');
    const [modalMessage, setModalMessage] = useState('');
    const [editingUser, setEditingUser] = useState<UserType | null>(null);

    // Role filter
    const [roleFilter, setRoleFilter] = useState('');

    const access = useAccessCheck();

    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const response = await apiClient.get('/options/roles');
                setRoles(Array.isArray(response.data) ? response.data : []);
            } catch (error: any) {
                showError(error);
            }
        };
        fetchRoles();
    }, [showError]);

    const roleFilterOptions = useMemo(
        () =>
            roles.map((role: any) => ({
                value: role.name || role.id,
                label: role.name || `Role ${role.id}`,
            })),
        [roles]
    );

    const statusFilterOptions = useMemo(
        () => [
            { value: '1', label: 'Active' },
            { value: '0', label: 'Inactive' },
            { value: '2', label: 'Suspended' },
        ],
        []
    );

    const tableOptions: DataTableOptions = useMemo(
        () => ({
            dataFields: {
                user_login: {
                    name: 'Username',
                    withSort: true,
                    maxWidth: '150px',
                },
                user_email: {
                    name: 'Email',
                    withSort: true,
                    maxWidth: '250px',
                },
                role_name: {
                    name: 'Role',
                    withSort: true,
                    withFilter: true,
                    filterOptions: roleFilterOptions,
                    maxWidth: '150px',
                },
                user_status: {
                    name: 'Status',
                    withSort: true,
                    withFilter: true,
                    filterOptions: statusFilterOptions,

                    badge: {
                        '1': 'green',
                        '0': 'yellow',
                        '2': 'red',
                    },
                    badgeLabels: {
                        '1': 'Active',
                        '0': 'Inactive',
                        '2': 'Suspended',
                    },
                },
                updated_at: {
                    name: 'Updated At',
                    withSort: true,
                    maxWidth: '150px',
                },
            },
            dataSource: activeTab === 'trash' ? '/user-management/archived/users' : '/user-management/users',
            primaryKey: 'id',
            softDelete: true,
            displayInModal: true,
            noRecordText: 'No users found',
            otherActions:
                activeTab === 'all'
                    ? [
                          {
                              name: 'View Activity',
                              link: '/user-management/user-activity',
                          },
                      ]
                    : undefined,
        }),
        [activeTab, roleFilterOptions, statusFilterOptions]
    );

    const permissions = useMemo(
        () => ({
            can_edit: access.can_edit,
            can_delete: access.can_delete,
            can_create: access.can_create,
            can_view: access.can_view,
            can_export: access.can_export,
            can_import: access.can_import,
        }),
        [access]
    );

    const showSubSubCallback = useMemo(
        () => (all: number, trashed: number) => {
            setTotalRows(all);
            setTotalTrash(trashed);
        },
        []
    );

    const tableParams = useMemo(() => {
        const params: any = { search: searchQuery };
        if (roleFilter) {
            params.role_name = roleFilter;
        }
        return params;
    }, [searchQuery, roleFilter]);

    const handleTabChange = (index: number) => {
        setActiveTab(index === 0 ? 'all' : 'trash');
        setSearchQuery('');
        if (searchRef.current) {
            searchRef.current.value = '';
        }
        tableRef.current?.clearPage();
    };

    const handleSearch = () => {
        const query = searchRef.current?.value || '';
        setSearchQuery(query);
        tableRef.current?.applyFilter('search', query);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleSearch();
        }
    };
    const handleOpenModal = (user?: UserType) => {
        setEditingUser(user || null);
        onOpen();
    };

    const handleCloseModal = () => {
        setEditingUser(null);
        onClose();
    };
    const handleSaveUser = async (formData: Record<string, any>) => {
        try {
            const payload: Record<string, any> = {
                user_login: `${formData.first_name.toLowerCase()}.${formData.last_name.toLowerCase()}`,
                user_email: formData.user_email,
                user_phone_number: formData.phone_number,
                user_role: formData.user_role,
                employee_id: formData.employee_id ?? '',
                user_firstname: formData.first_name,
                user_lastname: formData.last_name,
                user_fullname: formData.first_name + ' ' + formData.last_name,
                position: formData.department,
                department: formData.department,
                user_status: formData.user_status,
            };
            console.log('Payload before password check:', payload);
            if (!editingUser || (formData.user_pass && formData.user_pass.trim() !== '')) {
                payload.user_pass = formData.user_pass;
            }

            if (editingUser) {
                await apiClient.put(`/user-management/users/${editingUser.id}`, payload);
                showSuccess('Success', 'User updated successfully');
            } else {
                await apiClient.post('/user-management/users', payload);
                showSuccess('Success', 'User created successfully');
            }

            tableRef.current?.reload();
            handleCloseModal();
        } catch (error: any) {
            showError(error);
        }
    };

    // Edit user (fetch data and open modal)
    const handleEditUser = async (user: UserType) => {
        try {
            const response = await apiClient.get(`/user-management/users/${user.id}`);
            const userData = response.data?.data || response.data;

            // Transform user_role to ID string for UserForm
            let roleId = '';
            if (userData.user_role) {
                if (typeof userData.user_role === 'string') {
                    try {
                        roleId = JSON.parse(userData.user_role).id?.toString() || '';
                    } catch {
                        roleId = roles.find((r) => r.name === userData.user_role)?.id.toString() || '';
                    }
                } else if (userData.user_role.id) {
                    roleId = userData.user_role.id.toString();
                }
            } else if (userData.role_id) {
                roleId = userData.role_id.toString();
            }

            const userToEdit: UserType = {
                ...userData,
                user_role: roleId,
            };
            console.log('Taetaet', userToEdit);
            setEditingUser(userToEdit);
            onOpen();
        } catch (error: any) {
            showError(error);
        }
    };

    // Commented out for future use
    /*
  const handleBulkActionClick = () => {
    if (!bulkAction) {
      showWarning("Please select an action first");
      return;
    }
    const selectedIds = tableRef.current?.getSelectedRows() || [];
    if (selectedIds.length === 0) {
      showWarning("Please select at least one item");
      return;
    }

    setModalAction("bulk");
    if (bulkAction === "delete" && activeTab === "trash") {
      setModalMessage(
        "You are about to permanently delete these items from your site. This action cannot be undone.",
      );
    } else if (bulkAction === "reset_password") {
      setModalMessage(
        "Password reset emails will be sent to selected users. Continue?",
      );
    } else {
      setModalMessage("Are you sure to apply this change?");
    }
    onOpen();
  };
  */

    // Commented out for future use
    /*
  const handleChangeRoleClick = () => {
    if (!selectedRole) {
      showWarning("Please select a role first");
      return;
    }
    const selectedIds = tableRef.current?.getSelectedRows() || [];
    if (selectedIds.length === 0) {
      showWarning("Please select at least one item");
      return;
    }
    setModalAction("role");
    setModalMessage(
      "Are you sure you want to change the role for selected users?",
    );
    onOpen();
  };
  */

    // Commented out for future use
    /*
  const handleConfirmBulkAction = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || [];
    if (selectedIds.length === 0) return;

    setIsBulkProcessing(true);
    try {
      let url = "";
      let payload: any = { ids: selectedIds };
      let successMessage = "Action completed successfully";

      if (bulkAction === "restore") {
        url = "/user-management/users/bulk/restore";
        successMessage = "Users restored successfully";
      } else if (bulkAction === "delete") {
        url =
          activeTab === "trash"
            ? "/user-management/users/bulk/force-delete"
            : "/user-management/users/bulk/delete";
        successMessage =
          activeTab === "trash"
            ? "Users permanently deleted successfully"
            : "Users moved to trash successfully";
      } else if (bulkAction === "reset_password") {
        url = "/user-management/users/bulk/password";
        successMessage = "Password reset emails sent successfully";
      }

      await apiClient.post(url, payload);
      showSuccess("Success", successMessage);
      tableRef.current?.reload();
      setBulkAction("");
      onClose();
    } catch (error: any) {
      showError(error);
    } finally {
      setIsBulkProcessing(false);
    }
  };
  */

    // Commented out for future use
    /*
  const handleConfirmRoleChange = async () => {
    const selectedIds = tableRef.current?.getSelectedRows() || [];
    if (selectedIds.length === 0 || !selectedRole) return;

    setIsBulkProcessing(true);
    try {
      await apiClient.post("/user-management/users/bulk/role", {
        ids: selectedIds,
        role: selectedRole,
      });
      showSuccess("Success", "User role has been changed");
      tableRef.current?.reload();
      setSelectedRole("");
      onClose();
    } catch (error: any) {
      showError(error);
    } finally {
      setIsBulkProcessing(false);
    }
  };
  */

    // Commented out for future use
    /*
  const handleImport = () => {
    fileInputRef.current?.click();
  };
  */

    // Commented out for future use
    /*
  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      if (event.target) {
        event.target.value = "";
      }
      return;
    }

    setIsImporting(true);
    try {
      await importFromCSV({
        endpoint: "/user-management/users/import",
        file,
        showError,
        showSuccess,
        showWarning,
        onSuccess: () => {
          tableRef.current?.reload();
        },
      });
    } finally {
      setIsImporting(false);
      if (event.target) {
        event.target.value = "";
      }
    }
  };
  */

    // Commented out for future use
    /*
  const handleDownloadTemplate = () => {
    downloadCSVTemplate({
      csvHeaders: [
        "Username",
        "Email",
        "Password",
        "First Name",
        "Last Name",
        "Employee ID",
        "Position",
        "Role ID",
        "Status",
      ],
      exampleRow: [
        "johndoe",
        "john.doe@example.com",
        "TempPass123!",
        "John",
        "Doe",
        "EMP001",
        "Software Developer",
        "2",
        "1",
      ],
      comments: [
        "Note: Status values: 1 = Active, 0 = Inactive, 2 = Suspended",
        "Role ID should match an existing role ID in the system",
      ],
      filename: "users_template.csv",
      showError,
      showSuccess,
    });
  };
  */

    // Commented out for future use
    /*
  const handleExportData = async () => {
    setIsExporting(true);
    try {
      await exportToCSV({
        endpoint: "/user-management/users",
        selectedIds: [],
        activeTab,
        searchQuery: "",
        csvHeaders: [
          "ID",
          "Username",
          "Email",
          "First Name",
          "Last Name",
          "Employee ID",
          "Position",
          "Role",
          "Role ID",
          "Status",
          "Status Code",
          "Created At",
          "Updated At",
          "Deleted At",
        ],
        dataMapper: (user: any) => [
          user.id || "",
          user.user_login || "",
          user.user_email || "",
          user.first_name || user.user_details?.first_name || "",
          user.last_name || user.user_details?.last_name || "",
          user.employee_id || user.user_details?.employee_id || "",
          user.position || user.user_details?.position || "",
          user.role_name || "Unassigned",
          user.role_id || "",
          user.user_status === 1
            ? "Active"
            : user.user_status === 0
              ? "Inactive"
              : "Suspended",
          user.user_status || "",
          user.created_at || "",
          user.updated_at || "",
          user.deleted_at || "",
        ],
        filename: `users_export_all${activeTab === "trash" ? "_trash" : ""}_${new Date().toISOString().split("T")[0]}.csv`,
        showError,
        showSuccess,
        showWarning,
      });
    } catch (error: any) {
      showError(error);
    } finally {
      setIsExporting(false);
    }
  };
  */

    // Commented out for future use
    /*
  const handleConfirm = () => {
    if (modalAction === "bulk") {
      handleConfirmBulkAction();
    } else {
      handleConfirmRoleChange();
    }
  };
  */

    return (
        <AdminLayout>
            <Box h='calc(100vh - 110px)' overflow='hidden' display='flex' flexDirection='column' bg='#FEFCF8' py={1} px={{ base: 3, md: 5 }}>
                <VStack spacing={2} align='stretch' flex='1' minH={0}>
                    <Flex direction='column' flex='1' minH={0}>
                        {/* Header Section */}
                        <Box pt={6} pb={0} flexShrink={0}>
                            <Flex justify='space-between' align='flex-end' gap={4} width='100%' mb={6}>
                                <Tabs index={activeTab === 'all' ? 0 : 1} onChange={handleTabChange}>
                                    <TabList borderBottom='none'>
                                        {/* Commented out for future use - Tab interface */}
                                        {/*
                    <Tab>
                      All{' '}
                      <Badge ml={2} colorScheme="blue">
                        {totalRows}
                      </Badge>
                    </Tab>
                    {totalTrash > 0 && (
                      <Tab>
                        Trash{' '}
                        <Badge ml={2} colorScheme="red">
                          {totalTrash}
                        </Badge>
                      </Tab>
                    )}
                    */}

                                        <Badge color='#2E37A4' bg='#ECEDF7' fontSize='sm' px={3} py={1} borderRadius='md' fontWeight='medium' variant='outline' textTransform='none'>
                                            Total Users: {''}
                                            {totalRows}
                                        </Badge>
                                    </TabList>
                                </Tabs>

                                <HStack spacing={3} align='flex-end'>
                                    {/* Search Input */}
                                    <Box flex='0 1 auto' w='250px' h='32px' bg='white'>
                                        <InputGroup size='sm' h='full' borderRadius='md' border='1px' borderColor='gray.200'>
                                            <InputLeftElement h='full' pl='12px'>
                                                <IconButton aria-label='Search' icon={<Search size={16} />} size='sm' h='full' w='full' minW='24px' onClick={handleSearch} variant='ghost' color='gray.400' />
                                            </InputLeftElement>
                                            <Input
                                                ref={searchRef}
                                                placeholder='Search'
                                                onKeyDown={handleKeyDown}
                                                defaultValue={searchQuery}
                                                h='full'
                                                pl='48px'
                                                pr='6px'
                                                py='6px'
                                                fontSize='sm'
                                                borderRadius='md'
                                                border='1px'
                                                borderColor='gray.100'
                                                _hover={{ borderColor: '#DC3611' }}
                                                _focus={{
                                                    borderColor: '#DC3611',
                                                    boxShadow: '0 0 0 1px #DC3611',
                                                }}
                                            />
                                        </InputGroup>
                                    </Box>

                                    <Box pb={0}>
                                        <HStack spacing={2}>
                                            {/* Filter */}
                                            <Menu>
                                                <MenuButton as={Button} fontSize={{ base: 'xs', lg: 'sm' }} leftIcon={<Filter size={16} />} h='32px' bg='#FFFFFF' _hover={{ bg: '#E7E8EB' }} _active={{ bg: '#E7E8EB' }} color='black' variant='outline' border='1px' borderColor='#E7E8EB' flexShrink={0} whiteSpace='nowrap'>
                                                    Filter
                                                </MenuButton>
                                                <MenuList>
                                                    <MenuItem
                                                        onClick={() => {
                                                            setRoleFilter('');
                                                            tableRef.current?.applyFilter('role_name', '');
                                                        }}
                                                    >
                                                        All Roles
                                                    </MenuItem>
                                                    {roleFilterOptions.map((option) => (
                                                        <MenuItem
                                                            key={option.value}
                                                            onClick={() => {
                                                                setRoleFilter(option.value);
                                                                tableRef.current?.applyFilter('role_name', option.value);
                                                            }}
                                                        >
                                                            {option.label}
                                                        </MenuItem>
                                                    ))}
                                                </MenuList>
                                            </Menu>

                                            {/* Permission */}
                                            <Button fontSize={{ base: 'xs', lg: 'sm' }} leftIcon={<KeySquare size={16} />} h='32px' onClick={() => navigate('/user-management/roles')} bg='#06AED4' _hover={{ bg: '#059BBD' }} color='white' variant='outline' border='1px' borderColor='white' flexShrink={0} whiteSpace='nowrap'>
                                                Permission
                                            </Button>
                                            {/* Create New User */}
                                            <Button fontSize={{ base: 'xs', lg: 'sm' }} leftIcon={<Plus size={16} />} h='32px' onClick={() => handleOpenModal()} bg='#DC3611' _hover={{ bg: '#bf3c1fff' }} color='white' flexShrink={0} whiteSpace='nowrap'>
                                                New User
                                            </Button>
                                        </HStack>
                                    </Box>
                                </HStack>
                            </Flex>
                        </Box>

                        {/* Commented out for future use - Bulk Actions Section */}
                        {/*
            <CardHeader py={1} pt={3} pb={2} flexShrink={0}>
              <HStack spacing={4} flexWrap="wrap">
                {access.can_delete && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Bulk actions"
                      size="sm"
                      value={bulkAction}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setBulkAction(e.target.value)
                      }}
                    >
                      {activeTab === 'trash' && <option value="restore">Restore</option>}
                      {activeTab === 'trash' && <option value="delete">Delete Permanently</option>}
                      {activeTab === 'all' && <option value="delete">Delete</option>}
                      {activeTab === 'all' && <option value="reset_password">Send password reset</option>}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleBulkActionClick}>
                      Apply
                    </Button>
                  </HStack>
                )}

                {activeTab === 'all' && access.can_edit && (
                  <HStack spacing={2} minW="250px">
                    <Select
                      placeholder="Change role to..."
                      size="sm"
                      value={selectedRole}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        e.stopPropagation()
                        setSelectedRole(e.target.value)
                      }}
                    >
                      {roles.map((role: any) => (
                        <option key={role.id} value={role.id}>
                          {role.name}
                        </option>
                      ))}
                    </Select>
                    <Button size="sm" colorScheme="blue" onClick={handleChangeRoleClick}>
                      Change
                    </Button>
                  </HStack>
                )}
              </HStack>
            </CardHeader>
            */}

                        {/* Commented out for future use - ActionButtons Component */}
                        {/*
            <Box pb={2}>
              <ActionButtons
                pathname="/user-management/users"
                onExport={handleExportData}
                onImport={handleImport}
                onDownloadTemplate={handleDownloadTemplate}
                createLabel="Create New User"
                isExporting={isExporting}
                isImporting={isImporting}
                fileInputRef={fileInputRef}
                onFileUpload={handleFileUpload}
                acceptFileTypes=".csv"
              />
            </Box>
            */}

                        {/* DataTable Section */}
                        <Box flex='1' overflow='hidden' display='flex' flexDirection='column' minH={0} position='relative'>
                            <Box h='100%' display='flex' flexDirection='column' minH={0}>
                                <DataTable
                                    ref={tableRef}
                                    options={tableOptions}
                                    params={tableParams}
                                    access={permissions}
                                    setSubSub={showSubSubCallback}
                                    onOpenModal={handleEditUser}
                                    filterOptions={{
                                        role_name: roleFilterOptions,
                                        user_status: statusFilterOptions,
                                    }}
                                    customRenderers={{
                                        user_status: (value) => {
                                            let colorScheme = 'gray';
                                            let label = 'Unknown';

                                            if (value === '1' || value === 1) {
                                                colorScheme = 'green';
                                                label = 'Active';
                                            } else if (value === '0' || value === 0) {
                                                colorScheme = 'yellow';
                                                label = 'Inactive';
                                            } else if (value === '2' || value === 2) {
                                                colorScheme = 'red';
                                                label = 'Suspended';
                                            }

                                            return (
                                                <Badge colorScheme={colorScheme} variant='outline' fontSize='xs' px={2} py={1} borderRadius='md' textTransform='none'>
                                                    {label}
                                                </Badge>
                                            );
                                        },
                                    }}
                                />
                            </Box>
                        </Box>
                    </Flex>
                </VStack>
            </Box>

            {/* Commented out for future use - Confirmation Modal */}
            {/*
      <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Confirmation</ModalHeader>
          <ModalBody>
            <Text>{modalMessage}</Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose} isDisabled={isBulkProcessing}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleConfirm} isLoading={isBulkProcessing} loadingText="Processing...">
              Confirm
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
      */}
            <UserModal isOpen={isOpen} onClose={handleCloseModal} onSave={handleSaveUser} editingUser={editingUser} />
        </AdminLayout>
    );
}
